import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import { storage } from "util/storage";
import {
  isPushNotificationSupported,
  registerServiceWorker,
  requestNotificationPermission,
  subscribeToPushNotifications,
} from "util/pushNotifications";
import type { QuizUiStrings } from "i18n/quizUi";

const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
const DISMISS_KEY = "quiz-push-banner-dismissed";

interface QuizPushNotificationBannerProps {
  ui: QuizUiStrings;
}

/**
 * Lightweight push opt-in banner for the quiz route.
 */
const QuizPushNotificationBanner: React.FC<QuizPushNotificationBannerProps> = ({ ui }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(true);
  const [isDismissed, setIsDismissed] = useState(true);
  const [isEnabling, setIsEnabling] = useState(false);

  useEffect(() => {
    const dismissed = storage.getJSONWithTTL<boolean>(DISMISS_KEY, false);
    setIsDismissed(dismissed);

    const checkPushState = async () => {
      const supported = isPushNotificationSupported();
      setIsSupported(supported);
      if (!supported || !("serviceWorker" in navigator)) {
        setHasSubscription(true);
        return;
      }
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setHasSubscription(Boolean(subscription));
      } catch (_error) {
        setHasSubscription(false);
      }
    };

    void checkPushState();
  }, []);

  if (!isSupported || hasSubscription || isDismissed) {
    return null;
  }

  return (
    <div className="mx-auto mt-4 flex w-full max-w-6xl items-start justify-between gap-3 rounded-2xl border border-eoe-border-main bg-eoe-bg-modal px-4 py-3 shadow-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
      <div className="flex min-w-0 items-start gap-3">
        <div className="rounded-full bg-eoe-blue/10 p-2 text-eoe-blue dark:bg-eoe-blue/20">
          <Bell className="size-4" aria-hidden />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.pushBannerTitle}</p>
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.pushBannerDescription}</p>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => {
            void (async () => {
              setIsEnabling(true);
              try {
                const permission = await requestNotificationPermission();
                if (permission !== "granted") {
                  toast.error(ui.genericSaveError);
                  return;
                }
                const registration = await registerServiceWorker();
                if (!registration) {
                  toast.error(ui.genericSaveError);
                  return;
                }
                const subscription = await subscribeToPushNotifications(registration);
                if (!subscription) {
                  toast.error(ui.genericSaveError);
                  return;
                }
                setHasSubscription(true);
              } finally {
                setIsEnabling(false);
              }
            })();
          }}
          disabled={isEnabling}
          className="btn-primary inline-flex min-h-[40px] items-center justify-center px-4 py-2"
        >
          {ui.pushBannerAction}
        </button>
        <button
          type="button"
          onClick={() => {
            setIsDismissed(true);
            storage.setJSONWithTTL(DISMISS_KEY, true, DISMISS_TTL_MS);
          }}
          className="rounded-full p-1 text-eoe-text-sub hover:bg-eoe-bg-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-bg-hover"
          aria-label={ui.dismissPushBannerAction}
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default QuizPushNotificationBanner;
